// frontend/src/widgets/QuoteInboxWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-27
// Updated:      2026-09-01
//
// Description:
//   Quotes inbox/list widget.
//   - Loads visible quotes via GET /api/quotes
//   - Search (client-side) by title/customer/status/currency/notes/ids/opportunity/job refs
//   - Client-side lifecycle status filter
//   - Relationship-aware title: Quote ID + optional Opportunity ID + linked Job ref(s)
//   - Single click: select quote only
//   - Double click OR Edit button: open QuoteCreateModal in edit mode via onEditQuote
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//
// FIX (2026-02-09):
//   - HARD FIX for multi-click Edit button:
//     react-grid-layout captures mousedown and swallows click.
//     We must stop propagation on mousedown / pointerdown / touchstart
//     for ALL interactive controls (same pattern as other widgets).
//
// FIX (2026-02-10):
//   - Tighten stopGridDrag to include preventDefault (more reliable on some browsers).
//
// FIX (2026-03-25):
//   - Search input no longer uses preventDefault on pointer/mouse down, so focus/caret works again.
//   - Quote loader now tolerates array and common wrapped payload shapes.
//   - Client-side sort ensures newest quotes surface first even if backend ordering changed.
//   - Load limit increased to reduce silent truncation of newer records.
//
// Update (2026-05-08):
//   - Adds accepted / withdrawn lifecycle colours.
//   - Accepted is green.
//   - Withdrawn is red, same risk treatment as rejected.
//
// Update (2026-09-01):
//   - Adds client-side quote status filtering.
//   - Adds relationship-aware Quote Inbox labels without mutating stored quote titles.
//   - Surfaces opportunity_id and backend-enriched job_refs.
//   - Search now includes opportunity IDs and linked Job references.
//   - Long relationship labels wrap within the available row width instead of widening the widget.

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

const stopGridDrag = (e) => {
  try {
    e.preventDefault?.();
  } catch {}
  e.stopPropagation();
};

const stopGridDragNoPrevent = (e) => {
  e.stopPropagation();
};

function safeTrunc(s, max = 80) {
  const t = String(s || "").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return t.slice(0, max - 1).trimEnd() + "…";
}

function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const cur = String(currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: cur,
    }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

const normStatus = (v) => String(v || "").trim().toLowerCase();

const QUOTE_STATUSES = [
  "draft",
  "submitted",
  "approved",
  "sent",
  "accepted",
  "rejected",
  "expired",
  "withdrawn",
];

function titleCase(s) {
  const labels = {
    draft: "Draft",
    submitted: "Submitted",
    approved: "Approved",
    rejected: "Rejected",
    sent: "Sent",
    accepted: "Accepted",
    expired: "Expired",
    withdrawn: "Withdrawn",
  };

  const k = normStatus(s);
  if (labels[k]) return labels[k];

  return String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusPillStyle(theme, status) {
  const s = normStatus(status);

  const border = "rgba(0,0,0,0.10)";
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 999,
    padding: "5px 10px",
    fontWeight: 950,
    fontSize: 11,
    lineHeight: "11px",
    border: `1px solid ${border}`,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const redStatuses = new Set(["rejected", "withdrawn"]);
  const greenStatuses = new Set(["approved", "accepted"]);

  const bg = greenStatuses.has(s)
    ? "rgba(22,163,74,0.12)"
    : redStatuses.has(s)
    ? "rgba(220,38,38,0.12)"
    : s === "sent"
    ? "rgba(37,99,235,0.12)"
    : s === "submitted"
    ? "rgba(14,165,233,0.12)"
    : s === "expired"
    ? "rgba(107,114,128,0.12)"
    : "rgba(255,122,0,0.12)";

  const dot = greenStatuses.has(s)
    ? "#16a34a"
    : redStatuses.has(s)
    ? "#dc2626"
    : s === "sent"
    ? "#2563eb"
    : s === "submitted"
    ? "#0ea5e9"
    : s === "expired"
    ? "#6b7280"
    : theme?.accent_color || "#ff7a00";

  const fg = greenStatuses.has(s)
    ? "#166534"
    : redStatuses.has(s)
    ? "#991b1b"
    : s === "sent"
    ? "#1e3a8a"
    : s === "submitted"
    ? "#075985"
    : s === "expired"
    ? "#374151"
    : "#7c2d12";

  return { base, bg, dot, fg };
}

function extractQuoteList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.quotes)) return payload.quotes;
  return [];
}

function getSortTime(x) {
  const candidates = [
    x?.updated_at,
    x?.updatedAt,
    x?.created_at,
    x?.createdAt,
    x?.issued_at,
    x?.issuedAt,
  ];

  for (const v of candidates) {
    const ts = Date.parse(v);
    if (Number.isFinite(ts)) return ts;
  }

  const idNum = Number(x?.id);
  return Number.isFinite(idNum) ? idNum : 0;
}

function normaliseJobRefs(x) {
  const raw = Array.isArray(x?.job_refs)
    ? x.job_refs
    : Array.isArray(x?.jobRefs)
    ? x.jobRefs
    : [];

  return raw
    .map((job) => ({
      id: job?.id,
      job_ref: String(job?.job_ref ?? job?.jobRef ?? "").trim(),
    }))
    .filter((job) => job.job_ref);
}

function quoteRelationshipLabel(x) {
  const parts = [safeTrunc(x?.title) || `Quote #${x?.id}`, `Quote #${x?.id}`];

  const opportunityId = x?.opportunity_id ?? x?.opportunityId;
  if (opportunityId !== null && opportunityId !== undefined && String(opportunityId).trim() !== "") {
    parts.push(`Opp #${opportunityId}`);
  }

  const jobs = normaliseJobRefs(x);
  if (jobs.length === 1) {
    parts.push(`Job ${jobs[0].job_ref}`);
  } else if (jobs.length > 1) {
    parts.push(`Jobs ${jobs.map((job) => job.job_ref).join(", ")}`);
  }

  return parts.join(" · ");
}

export default function QuoteInboxWidget({
  selectedQuoteId,
  onSelectQuote,
  onEditQuote,
  refreshKey,
  customerMap,
}) {
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/api/quotes", {
        params: {
          limit: 1000,
          offset: 0,
          sort: "updated_at:desc",
        },
      });

      const rows = extractQuoteList(res?.data)
        .slice()
        .sort((a, b) => getSortTime(b) - getSortTime(a));

      setQuotes(rows);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load quotes");
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const filtered = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    const selectedStatus = normStatus(statusFilter);

    return quotes.filter((x) => {
      if (selectedStatus && normStatus(x?.status) !== selectedStatus) {
        return false;
      }

      if (!s) return true;

      const customerName =
        customerMap?.[String(x?.customer_id)] ||
        customerMap?.[String(x?.customerId)] ||
        x?.customer_name ||
        x?.customerName ||
        "";

      const opportunityId = x?.opportunity_id ?? x?.opportunityId;
      const jobRefs = normaliseJobRefs(x);

      return [
        x?.id,
        x?.quote_ref,
        x?.quoteRef,
        customerName,
        x?.status,
        x?.currency,
        x?.title,
        x?.public_notes,
        x?.publicNotes,
        x?.internal_notes,
        x?.internalNotes,
        x?.specific_notes,
        x?.specificNotes,
        x?.notes,
        x?.total,
        opportunityId,
        ...jobRefs.flatMap((job) => [job.id, job.job_ref]),
      ]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .join(" ")
        .toLowerCase()
        .includes(s);
    });
  }, [quotes, q, statusFilter, customerMap]);

  const openEdit = (id) => {
    if (!onEditQuote) return;
    onEditQuote({ quoteId: Number(id) });
  };

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Quote Inbox</div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, customer, quote, opportunity, job…"
          style={{
            width: "100%",
            minWidth: 0,
            padding: 10,
            borderRadius: 10,
            border: "1px solid #e5e7eb",
          }}
          onMouseDown={stopGridDragNoPrevent}
          onPointerDown={stopGridDragNoPrevent}
          onTouchStart={stopGridDragNoPrevent}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter quotes by status"
          title="Filter quotes by status"
          style={{
            padding: "10px 30px 10px 10px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
          onMouseDown={stopGridDragNoPrevent}
          onPointerDown={stopGridDragNoPrevent}
          onTouchStart={stopGridDragNoPrevent}
        >
          <option value="">All statuses</option>
          {QUOTE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {titleCase(status)}
            </option>
          ))}
        </select>
      </div>

      {loading && <div>Loading…</div>}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div style={{ color: "#6b7280", fontSize: 13 }}>No quotes found.</div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {filtered.map((x) => {
          const active = Number(selectedQuoteId) === Number(x.id);
          const custName =
            customerMap?.[String(x.customer_id)] ||
            customerMap?.[String(x.customerId)] ||
            x?.customer_name ||
            x?.customerName ||
            "Customer";

          const st = normStatus(x?.status);
          const pill = statusPillStyle(theme, st);

          return (
            <div
              key={x.id}
              onMouseDown={stopGridDrag}
              onPointerDown={stopGridDrag}
              onTouchStart={stopGridDrag}
              onClick={() => onSelectQuote?.(x.id)}
              onDoubleClick={() => openEdit(x.id)}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 10,
                padding: 10,
                borderRadius: 10,
                background: active ? "rgba(0,0,0,0.04)" : "transparent",
                border: active
                  ? `1px solid ${theme?.accent_color || "#ff7a00"}`
                  : "1px solid transparent",
                cursor: "pointer",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 900,
                    minWidth: 0,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "normal",
                    lineHeight: 1.35,
                  }}
                >
                  {quoteRelationshipLabel(x)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {custName} · {formatMoney(x.total, x.currency)}
                </div>
              </div>

              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  alignSelf: "start",
                  gap: 8,
                  flexShrink: 0,
                }}
                onMouseDown={stopGridDrag}
                onPointerDown={stopGridDrag}
                onTouchStart={stopGridDrag}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  style={{
                    ...pill.base,
                    background: pill.bg,
                    color: pill.fg,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: pill.dot,
                    }}
                  />
                  {titleCase(st || "draft")}
                </span>

                <button
                  type="button"
                  className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
                  style={{
                    borderRadius: 10,
                    padding: "6px 10px",
                    fontWeight: 900,
                    fontSize: 12,
                  }}
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(x.id);
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
